import React, {
  FunctionComponent, useCallback, useEffect, useRef, useState
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { RootState } from 'data/root.reducers';
import { GridSettings } from 'data/types';
import {
  EWARRANT_USER_CATEGORY_SETTINGS,
  EWarrantListItem,
  EWarrantPrivileges
} from 'data/eWarrant/eWarrant.types';
import * as actions from 'data/eWarrant/eWarrant.actions';
import * as selectors from 'data/eWarrant/eWarrant.selectors';
import {
  adaptEWarrantListItemsToGridRowData,
  eWarrantStatusValueLabels
} from 'data/eWarrant/eWarrant.adapter';
import CommonGrid from 'shared/commonGrid';
import { FilterModel } from 'shared/commonGrid/common-grid.types';
import { adaptToServerValueFilterModel } from 'shared/commonGrid/filterModel.adapter';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as userActions from 'users/users-actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as contactActions from 'data/contact/contact.actions';
import * as collectionFunctionGroupActions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as networkActions from 'data/network/network.actions';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as fqdnGroupActions from 'data/fqdnGroup/fqdnGroup.actions';
import * as nodeActions from 'data/nodes/nodes.actions';
import * as locationActions from 'data/location/location.actions';
import { AppToaster } from 'shared/toaster';
import EWarrantGrid from './components/EWarrantGrid';
import { eWarrantColumnDefs, eWarrantColumnDefsVersion } from '../../EWarrant.types';

type RouterParams = {
  history: string
};

const mapState = (state: RootState) => ({
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedEWarrants: selectors.getSelectedEWarrants(state),
});

const mapDispatch = {
  getEWarrantList: actions.getEWarrantList,
  selectEWarrant: actions.selectEWarrant,
  goToEWarrantView: actions.goToEWarrantView,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  // Collection function templates, and everything their options are built from. They
  // are resolved when a warrant is generated — see buildCollectionFunctionsFromEWarrant.
  getTemplates: warrantActions.getAllTemplates,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getCustomization: warrantActions.getWarrantFormCustomizationConfig,
  getTimezones: userActions.getTimezones,
  getContacts: contactActions.getAllContacts,
  getCollectionFunctionGroupList: collectionFunctionGroupActions.getCollectionFunctionGroupList,
  getCfFtpInfoList: cfFtpInfoActions.getCfFtpInfoList,
  getNetworks: networkActions.getNetworks,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getFqdnGroups: fqdnGroupActions.getFqdnGroups,
  getXcipioNodes: nodeActions.getNodes,
  fetchLocationList: locationActions.fetchLocationList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const EWarrantList: FunctionComponent<Props> = ({
  userName,
  privileges,
  beforeLogout,
  deliveryFunctionId,
  selectedEWarrants,
  history,
  getEWarrantList,
  selectEWarrant,
  goToEWarrantView,
  getGridSettings,
  saveGridSettings,
  getTemplates,
  getSupportedFeature,
  getCustomization,
  getTimezones,
  getContacts,
  getCollectionFunctionGroupList,
  getCfFtpInfoList,
  getNetworks,
  getSecurityInfos,
  getFqdnGroups,
  getXcipioNodes,
  fetchLocationList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const gridRef = useRef<CommonGrid>(null);

  const [selectedEWarrant] = selectedEWarrants;

  /**
   * Runs the list read the grid asked for. The grid builds the query out of its paging,
   * sorting and column filters, so every one of them is applied by the backend rather
   * than against rows already in the browser; the action still travels through the store,
   * which is where the view resolves the e-warrant behind a selected row from.
   */
  const handleGetResultsFromServer = useCallback((query: string) => {
    setLastUpdatedAt(new Date());
    return getEWarrantList(deliveryFunctionId, query);
  }, [deliveryFunctionId, getEWarrantList]);

  /**
   * Makes the grid re-read the list — on the refresh button, and when the delivery
   * function changes. Xcipio owns the e-warrant state, so the rows are always re-read
   * rather than patched in the store.
   */
  const reloadGrid = useCallback(() => {
    gridRef.current?.handleGridReloadFromServer(handleGetResultsFromServer, null);
  }, [handleGetResultsFromServer]);

  useEffect(() => {
    // The e-warrant read itself is left to the grid's datasource, which runs it on grid
    // ready and on every paging, sorting or filter change.
    //
    // Collection function templates are needed to generate a warrant. Generation runs
    // on the view page, which reads them out of the store rather than loading them
    // itself, so they are loaded here on the way in.
    getTemplates();
    getSupportedFeature(deliveryFunctionId);
    getCustomization(deliveryFunctionId);
    getTimezones();
    getContacts(deliveryFunctionId);
    getCollectionFunctionGroupList(deliveryFunctionId);
    getCfFtpInfoList(deliveryFunctionId);
    getNetworks(deliveryFunctionId);
    getSecurityInfos(deliveryFunctionId);
    getFqdnGroups(deliveryFunctionId);
    getXcipioNodes(deliveryFunctionId);
    fetchLocationList();

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getTemplates,
    getSupportedFeature,
    getCustomization,
    getTimezones,
    getContacts,
    getCollectionFunctionGroupList,
    getCfFtpInfoList,
    getNetworks,
    getSecurityInfos,
    getFqdnGroups,
    getXcipioNodes,
    fetchLocationList,
  ]);

  // The read is not scoped to a delivery function, but the e-warrants of one Xcipio are
  // not those of another, so a change of delivery function re-reads the list. Skipped on
  // mount: the grid's datasource has already run the first read by then.
  const hasReadOnce = useRef(false);

  useEffect(() => {
    if (hasReadOnce.current) {
      reloadGrid();
    }

    hasReadOnce.current = true;
  }, [deliveryFunctionId, reloadGrid]);

  /**
   * Turns the Status filter back into what the backend filters on. Its values are the
   * decision states the column shows (Pending), and Xcipio holds the AUTHSTATUS behind
   * them (AwaitingApproval) — the same dance the warrants grid does for its time zones.
   */
  const handleAdaptToFilterModel = (filterModel: FilterModel): FilterModel =>
    adaptToServerValueFilterModel(filterModel, 'status', eWarrantStatusValueLabels);

  const handleRefresh = () => reloadGrid();

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    // The grid renders the rows the backend returned, so a selected row is a raw list
    // item rather than the shape the actions and the action menu read it by.
    const selectedRows: EWarrantListItem[] = event.api.getSelectedRows();
    const previousId = selectedEWarrant?.id ?? '';
    const nextId = selectedRows.length ? selectedRows[0].hi1ObjectId : '';

    if (previousId !== nextId) {
      selectEWarrant(adaptEWarrantListItemsToGridRowData(selectedRows));
    }
  };

  const handleGetGridSettings = () =>
    getGridSettings(EWARRANT_USER_CATEGORY_SETTINGS, userName);

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(EWARRANT_USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleView = () => {
    if (selectedEWarrant != null) {
      goToEWarrantView(history);
    }
  };

  const hasPrivilegeToView = privileges.includes(EWarrantPrivileges.View);

  return (
    <EWarrantGrid
      ref={gridRef}
      selectedEWarrant={selectedEWarrant}
      columnDefs={eWarrantColumnDefs}
      columnDefsVersion={eWarrantColumnDefsVersion}
      noResults={false}
      beforeLogout={beforeLogout}
      lastUpdatedAt={lastUpdatedAt}
      hasPrivilegeToView={hasPrivilegeToView}
      onRefresh={handleRefresh}
      onSelectionChanged={handleSelectionChanged}
      onGetGridSettings={handleGetGridSettings}
      onSaveGridSettings={handleSaveGridSettings}
      onGetResultsFromServer={handleGetResultsFromServer}
      onAdaptToFilterModel={handleAdaptToFilterModel}
      onView={handleView}
    />
  );
};

export default withRouter(connector(EWarrantList));
